import { getLogger, type Logger } from "@expert/logging";
import type { Partner } from "@expert/shared-types";
import union from "lodash.union";
import type { JsonValue } from "type-fest";
import type { CaseDetails } from "@/shared-types";
import type { AgentSdk } from "../agentSdk";
import type { ChatMessage } from "../chat";
import type { Task } from "../task";
import type {
    AdvanceReadStatusOptions,
    AgentActivity,
    CallbackState,
    MutableAgentAttribute,
    ServiceProvider,
    SurveyMeta,
    TaskCompletedReason,
    WrappingEndReason,
    WrappingStartReason,
} from "../types";
import type { UpdateParticipantOptions } from "../voice/conferenceTypes";

function ensureAllFulfilled<T>(results: PromiseSettledResult<T>[]) {
    const reject = results.find((x): x is PromiseRejectedResult => x.status === "rejected");
    if (reject) {
        throw reject.reason;
    }
}

export class OrchestratorAgentSdk implements AgentSdk {
    readonly sdkType = "OrchestratorAgentSdk";

    readonly agentSdks: AgentSdk[];

    /**
     * @description this class manages multiple sdks the priority of the sdks is determines by the order of them.
     * priority is important for handling conflicts such as conflicting agent activities.
     * @param {AgentSdk[]} agentSdks the sdks the orchestrator will be managing, non-empty array, order determines priority
     */

    constructor(agentSdks: AgentSdk[]) {
        if (!agentSdks.length) {
            throw new Error("agentSdks has to have at least one sdk");
        }

        this.agentSdks = agentSdks;
        // Use first listed service provider as default service provider
        this.serviceProvider = agentSdks[0].serviceProvider;
    }
    serviceProvider: ServiceProvider;

    public logout(): void {
        for (const sdk of this.agentSdks) {
            sdk.logout?.();
        }
    }

    private getExactlyOneSupportingSDK(task: Task, logger?: Logger) {
        logger?.info({
            message: "Fetching supporting SDK for task: ",
            taskDetails: {
                type: task?.type,
                taskId: task?.id,
                sessionId: task?.sessionId,
                properties: Object.keys(task),
            },
            registeredSDKs: this.agentSdks.map((sdk) => sdk.sdkType),
        });

        const filteredSdks = this.agentSdks.filter((sdk) => {
            const canHandle = sdk.canHandleTaskType(task);
            logger?.info({
                message: "SDK can handle task: ",
                sdkName: sdk.sdkType,
                canHandleTask: canHandle,
            });
            return canHandle;
        });

        const { 0: taskSupportingSDK, length } = filteredSdks;

        logger?.info({
            message: "Filtered SDKs",
            filteredSDKs: filteredSdks.map((sdk) => sdk.sdkType),
            totalMatchingSDKs: length,
        });

        if (length !== 1) {
            logger?.error({
                message: `Task is supported by ${length} registered SDKs and should be supported by only one`,
                filteredSDKs: filteredSdks.map((sdk) => sdk.sdkType),
                taskDetails: {
                    type: task?.type,
                    taskId: task?.id,
                    sessionId: task?.sessionId,
                },
            });
            throw new RangeError(`Task is supported by ${length} registered SDKs and should be supported by only one`);
        }

        return taskSupportingSDK;
    }

    public getSdkTypeForTask(task: Task): string {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        return taskSupportingSDK.sdkType;
    }

    public canHandleTaskType(task: Task): boolean {
        return this.agentSdks.some((x) => x.canHandleTaskType(task));
    }

    public get agentActivities(): AgentActivity[] {
        return union(...this.agentSdks.map((agentSdk) => agentSdk.agentActivities));
    }

    public get availableAgentActivity(): AgentActivity {
        return this.getKnownAgentActivity("availableAgentActivity");
    }

    public get offlineAgentActivity(): AgentActivity {
        return this.getKnownAgentActivity("offlineAgentActivity");
    }

    public get defaultAgentActivity(): AgentActivity | undefined {
        return this.getKnownAgentActivity("defaultAgentActivity");
    }

    private getKnownAgentActivity<K extends keyof AgentSdk>(key: K): AgentSdk[K] {
        const set = new Set<AgentSdk[K]>();
        for (const agentSdk of this.agentSdks) {
            const value = agentSdk[key];
            if (value === undefined) continue;
            set.add(value);
            if (set.size > 1) throw new Error(`agent SDKs differ in their ${key}`);
        }
        const [result] = set;
        return result;
    }

    public isAgentActivitySelectable(activity: AgentActivity): boolean {
        return this.agentSdks.every((agentSdk) => agentSdk.isAgentActivitySelectable(activity));
    }

    public async setAgentActivity(activity: AgentActivity): Promise<void> {
        const results = await Promise.allSettled(this.agentSdks.map((x) => x.setAgentActivity(activity)));

        ensureAllFulfilled(results);
    }

    public getAgentActivity(): AgentActivity {
        const { availableAgentActivity } = this;
        return (
            this.agentSdks.map((x) => x.getAgentActivity()).find((x) => x !== availableAgentActivity) ??
            availableAgentActivity
        );
    }

    public async updateAgentAttributes(attributes: Record<MutableAgentAttribute, JsonValue>): Promise<void> {
        const results = await Promise.allSettled(this.agentSdks.map((x) => x.updateAgentAttributes(attributes)));

        ensureAllFulfilled(results);
    }

    public getPartners(): ReadonlySet<Partner> {
        const partners = this.agentSdks.flatMap((x) => Array.from(x.getPartners().values()));
        return new Set<Partner>(partners);
    }

    public async acceptTask(task: Task): Promise<void> {
        const { availableAgentActivity, offlineAgentActivity } = this;
        if (this.agentSdks.some((x) => x.getAgentActivity() !== availableAgentActivity)) {
            throw new Error("AgentSdks have to be available to accept the call");
        }

        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);

        // Set to offline on other platforms so that they aren't routed calls.
        // If a custom "Busy" status is desired instead then a new partner config prop can be added to support such.
        const activityResults = await Promise.allSettled(
            this.agentSdks.filter((x) => x !== taskSupportingSDK).map((x) => x.setAgentActivity(offlineAgentActivity)),
        );
        ensureAllFulfilled(activityResults);

        await taskSupportingSDK.acceptTask(task);
    }

    public async rejectTask(task: Task): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.rejectTask(task);
    }

    public async wrapupTask(task: Task, reason: string): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.wrapupTask(task, reason);
    }

    public async completeTask(task: Task, reason: TaskCompletedReason): Promise<void> {
        const { availableAgentActivity } = this;
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);

        const activityResults = await Promise.allSettled(
            this.agentSdks
                .filter((x) => x !== taskSupportingSDK)
                .map((x) => x.setAgentActivity(availableAgentActivity)),
        );
        ensureAllFulfilled(activityResults);

        await taskSupportingSDK.completeTask(task, reason);
    }

    public async cancelTask(task: Task): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.cancelTask(task);
    }

    public async fetchContactsForTask(task: Task): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.fetchContactsForTask(task);
    }

    public async hangupCall(task: Task, reason: string): Promise<void> {
        const logger = getLogger({ module: "hangupCallSDKDebugging" });
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task, logger);
        await taskSupportingSDK.hangupCall(task, reason);
    }

    public areSurveysAutomatic(task: Task): boolean {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        return taskSupportingSDK.areSurveysAutomatic(task);
    }

    public async sendToSurvey(task: Task, surveyMeta?: SurveyMeta): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.sendToSurvey(task, surveyMeta);
    }

    public async holdCall(task: Task): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.holdCall(task);
    }
    public async resumeCall(task: Task): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.resumeCall(task);
    }

    public async muteCall(task: Task, shouldMute: boolean): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.muteCall(task, shouldMute);
    }

    public async sendDtmfDigits(digits: string): Promise<void> {
        const activityResults = await Promise.allSettled(this.agentSdks.map((x) => x.sendDtmfDigits(digits)));
        ensureAllFulfilled(activityResults);
    }

    public async redirectCallToTFN(task: Task, redirectTo: string): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.redirectCallToTFN(task, redirectTo);
    }

    public async redirectCallToSIP(task: Task, redirectTo: string): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.redirectCallToSIP(task, redirectTo);
    }

    public async addConferenceParticipant(
        task: Task,
        name: string,
        to: string,
        isSip?: boolean,
        sipParams?: Record<string, string>,
    ): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.addConferenceParticipant(task, name, to, isSip, sipParams);
    }
    public async updateParticipant(task: Task, callId: string, options: UpdateParticipantOptions): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.updateParticipant(task, callId, options);
    }
    public async removeParticipant(task: Task, callId: string): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.removeParticipant(task, callId);
    }
    public async removePendingParticipant(task: Task, callId: string): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.removePendingParticipant(task, callId);
    }
    public async leaveConference(task: Task): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.leaveConference(task);
    }
    public async endConference(task: Task): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.endConference(task);
    }

    public async resetConferenceState(task: Task): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        await taskSupportingSDK.resetConferenceState(task);
    }

    // Should we remove it from the interface and keep it in the SdkBase? interfaces are usually meant for public methods
    protected createCustomerCallbackForTask(_task: Task, _callbackState: CallbackState): Promise<string> {
        throw new Error("Method not implemented.");
    }

    // Should we remove it from the interface and keep it in the SdkBase? interfaces are usually meant for public methods
    protected createOutboundCall(_callbackMdn: string): Promise<string> {
        throw new Error("Method not implemented.");
    }

    public completeSession(partner: Partner): void {
        this.agentSdks.forEach((x) => {
            x.completeSession(partner);
        });
    }

    public async scheduleCallbackNow(callbackDelay: number, callbackMdn: string): Promise<boolean> {
        const [primarySdk] = this.agentSdks;
        return await primarySdk.scheduleCallbackNow(callbackDelay, callbackMdn);
    }

    public async callCustomer(mdn: string, options?: { extension?: string | undefined }): Promise<void> {
        const [primarySdk] = this.agentSdks;
        await primarySdk.callCustomer(mdn, options);
    }

    public async startWrapup(reason: WrappingStartReason, task: Task): Promise<void> {
        const [primarySdk] = this.agentSdks;
        await primarySdk.startWrapup(reason, task);
    }

    public async endWrapup(reason: WrappingEndReason, partner: Partner, task: Task): Promise<void> {
        const [primarySdk] = this.agentSdks;
        await primarySdk.endWrapup(reason, partner, task);
    }

    public async callCustomerBack(callbackState: CallbackState): Promise<void> {
        const [primarySdk] = this.agentSdks;
        await primarySdk.callCustomerBack(callbackState);
    }

    public async updateCaseDetails(task: Task, caseDetailsUpdates: Partial<CaseDetails>): Promise<void> {
        const [primarySdk] = this.agentSdks;
        await primarySdk.updateCaseDetails(task, caseDetailsUpdates);
    }

    public getCaseDetails(task: Task): CaseDetails | undefined {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        return taskSupportingSDK.getCaseDetails(task);
    }

    //#region Chat specific methods
    public async getMessages(task: Task): Promise<ChatMessage[]> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        return await taskSupportingSDK.getMessages(task);
    }

    public sendMessage(task: Task, message: string): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        return taskSupportingSDK.sendMessage(task, message);
    }

    public sendTypingIndicator(task: Task): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        return taskSupportingSDK.sendTypingIndicator(task);
    }

    public advanceReadStatus(task: Task, options: AdvanceReadStatusOptions): Promise<void> {
        const taskSupportingSDK = this.getExactlyOneSupportingSDK(task);
        return taskSupportingSDK.advanceReadStatus(task, options);
    }
    //#endregion
}
